import React, { ReactNode } from 'react'

import { FieldMetaProps } from 'formik/dist/types'
import styled from 'styled-components'
import { SpaceProps, WidthProps } from 'styled-system'

import { Box } from '@/atoms/layout'
import { Space } from '@/styles'

import { ErrorInput, ErrorMessage, ErrorValue } from '../error-message'
import { FieldLabel } from '../field-label'

interface Props<Value> {
  id?: string
  label?: ReactNode
  tooltip?: string
  meta?: FieldMetaProps<Value>
  fieldProps?: (SpaceProps & WidthProps) | WidthProps
  children: ReactNode
  hideErrorMessage?: boolean
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

type TransError = { transKey: string; values: Record<string, string | number> }

const isTransError = (v: unknown): v is TransError => {
  if (!isRecord(v)) return false
  return typeof v.transKey === 'string' && isRecord(v.values)
}

export const normalizeFormikError = (err: unknown): ErrorInput | undefined => {
  if (err == null || err === '') return undefined

  if (typeof err === 'string') return err
  if (isTransError(err)) return err

  if (Array.isArray(err)) {
    const items: ErrorValue[] = err.flatMap(e => {
      const n = normalizeFormikError(e)
      return !n ? [] : Array.isArray(n) ? n : [n]
    })
    return items.length ? items : undefined
  }

  if (isRecord(err)) {
    // Formik nested error objects: return first nested error (string or TransError)
    for (const v of Object.values(err)) {
      const n = normalizeFormikError(v)
      if (n) return n
    }
  }

  return undefined
}

// eslint-disable-next-line @typescript-eslint/comma-dangle
const FieldWrapper = <Value,>({
  label,
  tooltip,
  children,
  id,
  meta,
  fieldProps,
  hideErrorMessage = false,
}: Props<Value> & SpaceProps & WidthProps) => {
  const touched = !!(meta?.touched || (meta as any)?.initialTouched)
  const normalizedError = normalizeFormikError(meta?.error as any)
  const hasError = touched && !!normalizedError

  return (
    <Wrapper {...fieldProps} id={`${id}_field`}>
      {label && <FieldLabel id={id} label={label} tooltip={tooltip} mb={Space.S4} />}
      {children}
      {hasError && !hideErrorMessage && <ErrorMessage error={normalizedError} />}
    </Wrapper>
  )
}

const Wrapper = styled(Box)`
  line-height: 100%;

  &:hover > .css-18onqoq-container > .react-select__control {
    border-color: ${({ theme }) => theme.colors.violet};
  }

  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
  }
`

FieldWrapper.displayName = 'FieldWrapper'

export default FieldWrapper
